import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getChargingStationInfo, checkOneClickChargeEligibility } from "../services/apiService";
import analyticsService from "../services/analyticsService";
import { APP_STORE_URL, PLAY_STORE_URL } from "../constants/storeUrls";
import StatusScreen from "./ui/StatusScreen";

const REDIRECTION_PAGE_URL = process.env.REACT_APP_REDIRECTION_PAGE_URL;


export default function ChargeComponent() {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partner = queryParams.get("partner");
  const connectorParam = queryParams.get("connector");
  const serial = queryParams.get("serial");

  const [cpData, setCpData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showStoreRedirect, setShowStoreRedirect] = useState(false);
  const { ref: reference } = useParams();
  const navigate = useNavigate();

  const hasProcessedRedirection = useRef(false);

  useEffect(() => {
    if (hasProcessedRedirection.current) {
      return;
    }
    hasProcessedRedirection.current = true;

    const fetchChargePointDetails = async () => {
      try {
        // Récupérer les informations de la borne
        const evcpData = await getChargingStationInfo(reference);
        setCpData(evcpData);
        analyticsService.pushEvent('station_loaded', {
          station_id: evcpData.reference,
          partner: partner || 'QOVOLTIS',
        });

        // Vérifier l'éligibilité au One-Click Charge
        const eligibilityData = await checkOneClickChargeEligibility(evcpData.reference);

        if (!eligibilityData.eligibility) {
          analyticsService.pushEvent('station_not_eligible', { station_id: evcpData.reference });
          window.open(REDIRECTION_PAGE_URL, '_blank', 'noopener,noreferrer');
          setShowStoreRedirect(true);
          setIsLoading(false);
          return;
        }

        // filtrer les connecteurs pour masquer ceux de type E
        const filteredConnectors = evcpData.connectors.filter(
          connector => connector.type !== "DOMESTIC_E"
        );

        // Une fois le connecteur cible connu : reprise de session si CE connecteur
        // porte une transaction, sinon parcours de démarrage classique.
        // La détection ne doit jamais se faire au niveau de la borne : sur une borne
        // multi-connecteurs, une charge en cours sur l'un ne doit pas bloquer l'autre.
        const goToConnector = (connector) => {
          if (connector.hasTransaction) {
            analyticsService.pushEvent('active_session_detected', {
              station_id: evcpData.reference,
              connector_id: connector.reference,
            });
            // Écran 2a « Cette prise est occupée », puis les 4 chiffres (2b).
            navigate("/busy", {
              replace: true,
              state: {
                evcpData,
                connector,
                reference,
                partner: partner || "QOVOLTIS",
              },
            });
            return;
          }

          goToLanding(connector);
        };

        // Écran d'arrivée (1a / 3a) : étapes, prise et tarif. Une prise visée par
        // le QR code y est seule et présélectionnée. `replace` : le retour arrière
        // depuis l'e-mail ramène à l'arrivée, pas à cette page d'aiguillage.
        const goToLanding = (connector = null) => {
          navigate(`/connectors/${reference}`, {
            replace: true,
            state: {
              evcpData,
              eligibilityData,
              reference,
              partner: partner || "QOVOLTIS",
              connectorReference: connector?.reference || null,
            },
          });
        };

        if (connectorParam) {
          // Trouver le connecteur correspondant dans la liste des connecteurs
          const selectedConnector = filteredConnectors.find(
            connector => connector.reference === connectorParam
          );
          
          if (selectedConnector) {
            goToConnector(selectedConnector);
            return;
          }
        }

        // Une seule prise : il n'y a aucun choix à faire. Occupée, on va droit a la
        // saisie des 4 chiffres ; libre, le parcours de démarrage suit son cours.
        if (filteredConnectors.length === 1 && filteredConnectors[0].hasTransaction) {
          goToConnector(filteredConnectors[0]);
          return;
        }

        // Plusieurs prises et une recharge en cours : on ne présume plus de l'intention
        // du client, il choisit entre nouvelle recharge et reprise de session.
        const hasOngoingCharge = filteredConnectors.some(
          connector => connector.hasTransaction
        );

        if (hasOngoingCharge) {
          analyticsService.pushEvent('active_session_detected', {
            station_id: evcpData.reference,
            ongoing_connectors: filteredConnectors.filter(c => c.hasTransaction).length,
          });
          navigate("/session-choice", {
            replace: true,
            state: {
              evcpData,
              eligibilityData,
              reference,
              partner: partner || "QOVOLTIS",
            },
          });
          return;
        }

        if (filteredConnectors.length > 0) {
          goToLanding();
        } else {
          navigate("/error", {
            state: { errorText: t("noBorneData") },
          });
        }
      } catch (err) {
        console.error("Erreur:", err);
        // apiService porte le code HTTP dans err.status.
        const status = err?.status ?? err?.response?.status;
        const isNotFound = status === 404;
        analyticsService.pushEvent(isNotFound ? 'station_not_found' : 'station_load_error', {
          station_id: reference,
          error_type: status || 'unknown',
        });
        // Borne inconnue : « Page introuvable ». Sinon erreur générique, rien
        // n'ayant été débité à ce stade.
        navigate("/error", { state: isNotFound ? { notFound: true } : {} });
      }
    };

    if (reference) {
      analyticsService.pushPageView('scan_to_charge_landing', { station_id: reference });
      fetchChargePointDetails();
    }
  }, [navigate, reference, partner, serial, connectorParam, t]);

  if (showStoreRedirect) {
    const storeBadge = (platform, src, alt, url) => (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => analyticsService.pushEvent("app_download_tapped", { platform })}
      >
        <img src={src} alt={alt} className="h-12" />
      </a>
    );

    return (
      <StatusScreen image="/Plug_Circle.svg" title={t("ineligible.title")} text={t("ineligible.text")}>
        <div className="flex flex-wrap justify-center gap-3">
          {storeBadge("ios", "/app-store-badge.svg", "App Store", APP_STORE_URL)}
          {storeBadge("android", "/google-play-badge.svg", "Google Play", PLAY_STORE_URL)}
        </div>
      </StatusScreen>
    );
  }

  if (isLoading) {
    return <StatusScreen loading title={t("loadingStation.title")} text={t("loadingStation.text")} />;
  }

  return null;
}
